import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.js';
import { Alert } from '../components/ui.js';
import { PasswordStrength } from '../components/PasswordStrength.js';

export function Register() {
  const { register, login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) return setError('Master password must be at least 12 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    if (!agree) return setError('Please confirm you understand the recovery policy.');
    setBusy(true);
    try {
      const { verificationRequired } = await register(email.trim().toLowerCase(), password, name || undefined);
      if (verificationRequired) {
        navigate('/verify-email', { state: { email } });
      } else {
        // No email verification configured — log straight into the new vault.
        try {
          await login(email.trim().toLowerCase(), password);
        } catch {
          navigate('/login', { state: { email } });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="panel auth-card">
        <div className="brand" style={{ padding: '0 0 18px' }}><span className="logo">🔐</span> Battini <span className="gradient-text">Vault</span></div>
        <h2 style={{ margin: '0 0 4px' }}>Create your vault</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>Your master password encrypts everything. Choose a strong one — we cannot reset it for you.</p>

        {error && <Alert kind="error">{error}</Alert>}

        <form onSubmit={submit}>
          <div className="field">
            <label>Email</label>
            <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label>Name (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label>Master password</label>
            <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            {password && <div className="mt-sm"><PasswordStrength password={password} /></div>}
          </div>
          <div className="field">
            <label>Confirm master password</label>
            <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <label className="flex" style={{ alignItems: 'flex-start', fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginRight: 8, marginTop: 4 }} />
            <span>I understand that if I lose my master password and recovery key, my vault cannot be recovered — this is the core of zero-knowledge security.</span>
          </label>
          <button className="btn full" disabled={busy}>{busy ? 'Creating vault…' : 'Create vault'}</button>
        </form>
        <div className="mt center" style={{ fontSize: 13 }}>
          <span className="muted">Already have an account? </span>
          <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
